/* eslint-env browser */
import React, { useState, useEffect } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { RichUtils } from 'draft-js';
import {
  getCurrentEntity,
  getSelectStateForEntity,
  createLink,
} from 'helpers/draftjs-utils';
import StyleButton from './StyleButton';
import styled from 'styled-components';
import { ButtonCondensed } from '@companycam/slab-web';
import { sanitizeUrl } from './utils';

const LinkInputContainer = styled.div`
  display: flex;
  gap: var(--cc_size_spacing_s);

  input {
    padding: 6px 8px !important;
    margin-left: 8px;
    font-size: 12px;
    line-height: 1.25;
  }
`;

const STYLES = [
  {
    toggleFunc: RichUtils.toggleInlineStyle,
    options: [
      {
        ariaLabel: 'Bold Text',
        label: 'B',
        style: 'BOLD',
        icon: 'format-bold',
      },
      {
        ariaLabel: 'Italic Text',
        label: 'I',
        style: 'ITALIC',
        icon: 'format-italic',
      },
    ],
  },
  {
    toggleFunc: RichUtils.toggleBlockType,
    options: [
      {
        ariaLabel: 'Bulleted List',
        label: 'UL',
        style: 'unordered-list-item',
        icon: 'format-list-bulleted',
      },
      {
        ariaLabel: 'Numbered List',
        label: 'OL',
        style: 'ordered-list-item',
        icon: 'format-list-numbered',
      },
    ],
  },
];

const messages = defineMessages({
  snippets: {
    id: 'RichTextEditor.toolbar.snippets',
    defaultMessage: 'Text Snippets',
    description: 'Text snippets button label',
  },
  addSnippet: {
    id: 'RichTextEditor.toolbar.addSnippet',
    defaultMessage: 'Add Text Snippet ',
    description: 'Add text snippet button label',
  },
  link: {
    id: 'RichTextEditor.toolbar.link',
    defaultMessage: 'Link',
    description: 'Link button label',
  },
  unlink: {
    id: 'RichTextEditor.toolbar.unlink',
    defaultMessage: 'Unlink',
    description: 'Unlink button label',
  },
});

const URL_DEFAULT_VALUE = 'https://';

const Toolbar = ({
  editorState,
  onStyleToggle,
  setEditorCloseable,
  snippetsVisible,
  handleAddSnippet,
  handleOpenSnippetModal,
  intl,
  urlInputRef,
}) => {
  const { formatMessage } = intl;
  const [showURLInput, setShowURLInput] = useState(false);

  const [urlValue, setURLValue] = useState(URL_DEFAULT_VALUE);

  const selection = editorState.getSelection();

  const currentEntity = getCurrentEntity(editorState);

  const currentStyle = editorState.getCurrentInlineStyle();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  const selectionCollapsed = selection.isCollapsed();
  const focusIsLink = !!currentEntity && currentEntity.getType() === 'LINK';

  useEffect(() => {
    if (showURLInput && selectionCollapsed && !focusIsLink) {
      setShowURLInput(false);
    }
  }, [showURLInput, selectionCollapsed, focusIsLink]);

  const toggleURLPrompt = () => {
    if (showURLInput) {
      setShowURLInput(false);
    } else {
      const url = focusIsLink ? currentEntity.getData().url : URL_DEFAULT_VALUE;
      setURLValue(url);
      setEditorCloseable(false);
      setShowURLInput(true);
      setTimeout(() => {
        urlInputRef.current.focus();
        setEditorCloseable(true);
      }, 0);
    }
  };

  const confirmLink = () => {
    if (urlValue && urlValue.length) {
      const sanitizedUrl = sanitizeUrl(urlValue);

      const key = createLink(editorState, sanitizedUrl);
      const selectionState = focusIsLink
        ? getSelectStateForEntity(editorState)
        : selection;

      onStyleToggle(RichUtils.toggleLink(editorState, selectionState, key));
      setShowURLInput(false);
    }
  };

  const removeLink = () => {
    const linkSelection = getSelectStateForEntity(editorState);
    onStyleToggle(RichUtils.toggleLink(editorState, linkSelection, null));
    setShowURLInput(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      confirmLink(e);
    }
    if (e.key === 'Escape') {
      setShowURLInput(false);
    }
  };

  const renderSnippetButton = () => {
    const child = (
      <StyleButton
        ariaLabel="Use Text Snippet"
        key="snippets"
        label={formatMessage({ id: 'RichTextEditor.toolbar.snippets' })}
        onToggle={null}
        onMouseDown={handleOpenSnippetModal}
        icon="playlist-star"
      />
    );

    return child;
  };

  let urlInput;

  if (showURLInput) {
    urlInput = (
      <LinkInputContainer>
        <input
          type="text"
          ref={urlInputRef}
          value={urlValue}
          onKeyDown={handleKeyDown}
          placeholder="Link URL"
          onChange={(e) => setURLValue(e.target.value)}
        />
        <ButtonCondensed color="primary" size="small" onClick={confirmLink}>
          {formatMessage(messages.link)}
        </ButtonCondensed>
        {!!currentEntity && currentEntity.getType() === 'LINK' && (
          <ButtonCondensed color="primary" size="small" onClick={removeLink}>
            {formatMessage(messages.unlink)}
          </ButtonCondensed>
        )}
      </LinkInputContainer>
    );
  }

  return (
    <div className="RichEditor-controls">
      {STYLES.map(({ toggleFunc, options }) =>
        options.map((option) => {
          return (
            <StyleButton
              ariaLabel={option.ariaLabel}
              key={option.label}
              active={
                blockType === option.style || currentStyle.has(option.style)
              }
              label={option.label}
              icon={option.icon}
              onToggle={(style) => {
                onStyleToggle(toggleFunc(editorState, style));
              }}
              style={option.style}
            />
          );
        }),
      )}

      {snippetsVisible && renderSnippetButton()}

      {!selectionCollapsed && snippetsVisible && (
        <StyleButton
          color="primary"
          ariaLabel="Create New Snippet"
          key="addSnippet"
          label={formatMessage({ id: 'RichTextEditor.toolbar.addSnippet' })}
          onToggle={null}
          icon="seal"
          onMouseDown={handleAddSnippet}
        />
      )}
      {(!selectionCollapsed || focusIsLink) && (
        <StyleButton
          ariaLabel="Create Link"
          key="link"
          active={showURLInput || focusIsLink}
          label="Link"
          icon="link-variant"
          onToggle={toggleURLPrompt}
          style="LINK"
        />
      )}
      {urlInput}
    </div>
  );
};

Toolbar.propTypes = {
  editorState: PropTypes.object,
  onStyleToggle: PropTypes.func,
  setEditorCloseable: PropTypes.func,
  snippetsVisible: PropTypes.bool,
  handleAddSnippet: PropTypes.func,
  handleOpenSnippetModal: PropTypes.func,
  intl: PropTypes.object,
  urlInputRef: PropTypes.object,
};

export default injectIntl(Toolbar);
